<!-- Mobile Section (Figma Design) — always light mode -->
<div class="bg-[#F8F9FB] dark:bg-surface-1 min-h-screen px-4 pb-24">
  <!-- Page Title & Date Filter -->
  <div class="flex items-center justify-between mb-6">
    <div>
      <h1 class="text-xl font-bold text-[#141437] dark:text-white">Monthly Report 2</h1>
      <p class="text-[10px] font-bold text-gray-400 dark:text-ink-soft uppercase tracking-[0.1em] mt-0.5">
        {{ startDate() | date:'MMM dd, yyyy' }} - {{ endDate() | date:'MMM dd, yyyy' }}
      </p>
    </div>
    <button (click)="openFilterSheet()"
      class="p-3 bg-white dark:bg-surface-1 rounded-full border border-gray-200 dark:border-line text-purple-500 dark:text-[#0C66E8] transition-all active:scale-95 shadow-sm">
      <app-icon name="filter" [size]="20"></app-icon>
    </button>
  </div>

  <!-- AI Headlines Widget -->
  @if (!mixedMode()) {
    <div class="mb-6 -mx-1">
      <ai-headlines></ai-headlines>
    </div>
  }

  <!-- Primary KPIs -->
  <div class="grid grid-cols-2 gap-4 mb-6">
    <div class="bg-white dark:bg-surface-0 dark:border-surface-0 border border-[#F5F5F5] rounded-3xl p-5 shadow-sm relative">
      <app-icon name="gripVertical" [size]="14" class="text-slate-300 absolute top-4 right-4"></app-icon>
      <div class="flex flex-col gap-3 mb-4">
        <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-500/20 flex items-center justify-center">
          <app-icon name="user" [size]="18" class="text-blue-600"></app-icon>
        </div>
        <span class="text-[12px] font-medium text-slate-500 dark:text-ink-soft">Enrolled Businesses</span>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-2xl font-bold text-slate-900 dark:text-white">{{ currentBusinessContractsOverall()?.total_enrolled || 0
          }}</span>
        <div class="flex items-center gap-1 text-[10px] font-bold">
          <div class="flex items-center gap-1 text-emerald-500">
            <app-icon name="trendingUp" [size]="12"></app-icon>
            <span>+18.2%</span>
          </div>
          <span class="text-slate-400 dark:text-ink-muted">vs last month</span>
        </div>
      </div>
    </div>
    <div class="bg-white dark:bg-surface-0 dark:border-surface-0 border border-[#F5F5F5] rounded-3xl p-5 shadow-sm relative">
      <app-icon name="gripVertical" [size]="14" class="text-slate-300 absolute top-4 right-4"></app-icon>
      <div class="flex flex-col gap-3 mb-4">
        <div class="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-500/20 flex items-center justify-center">
          <app-icon name="pencil" [size]="18" class="text-blue-600"></app-icon>
        </div>
        <span class="text-[12px] font-medium text-slate-500 dark:text-ink-soft">Contracts Created</span>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-2xl font-bold text-slate-900 dark:text-white">{{ currentBusinessContractsOverall()?.total_contracts || 0 |
          number }}</span>
        <div class="flex items-center gap-1 text-[10px] font-bold">
          <div class="flex items-center gap-1 text-emerald-500">
            <app-icon name="trendingUp" [size]="12"></app-icon>
            <span>+18.2%</span>
          </div>
          <span class="text-slate-400 dark:text-ink-muted">vs last month</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Training Status Card -->
  <div class="bg-white dark:bg-surface-0 dark:border-surface-0 border border-[#F5F5F5] rounded-3xl p-6 shadow-sm mb-6 relative" >
    <app-icon name="gripVertical" [size]="14" class="text-slate-300 absolute top-4 right-4"></app-icon>
    <div class="mb-6">
      <h3 class="text-sm font-bold text-slate-900 dark:text-ink-soft">Total Training Status</h3>
      <p class="text-4xl font-bold text-slate-900 dark:text-white mt-2">{{ getTotalCount() }}</p>
    </div>

    <!-- Multi-segmented Progress Bar -->
    <div class="h-4 w-full bg-slate-100 rounded-full overflow-hidden flex mb-8">
      @for (item of businessTrainingStatsData(); track $index) {
      <div [ngClass]="getBgColor($index)" [style.width.%]="(item.count / getTotalCount()) * 100"
        class="h-full border-r border-white last:border-0"></div>
      }
    </div>

    <!-- Status List -->
    <div class="grid grid-cols-1 gap-4">
      @for (item of businessTrainingStatsData(); track $index) {
      <div class="flex items-center justify-between group">
        <div class="flex items-center gap-3">
          <div [ngClass]="getBgColor($index)" class="w-2.5 h-2.5 rounded-full"></div>
          <span class="text-sm font-medium text-slate-600 dark:text-ink-soft">{{ item.status }}</span>
        </div>
        <span class="text-sm font-bold text-slate-900 dark:text-white">{{ item.count }}</span>
      </div>
      }
    </div>
  </div>

  <!-- Secondary KPIs -->
  <div class="grid grid-cols-2 gap-4 mb-8">
    <div class="bg-white dark:bg-surface-0 dark:border-surface-0 border border-[#F5F5F5] rounded-3xl p-5 shadow-sm relative">
      <app-icon name="gripVertical" [size]="14" class="text-slate-300 absolute top-4 right-4"></app-icon>
      <div class="flex flex-col gap-3 mb-4">
        <div class="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
          <app-icon name="fileText" [size]="18" class="text-white"></app-icon>
        </div>
        <span class="text-[12px] font-medium text-slate-500 dark:text-ink-soft">Activation Rate</span>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-2xl font-bold text-slate-900 dark:text-white">{{ currentBusinessContractsOverall()?.activation_rate || 0
          }}%</span>
        <div class="flex items-center gap-1 text-[10px] font-bold">
          <div class="flex items-center gap-1 text-emerald-500">
            <app-icon name="trendingUp" [size]="12"></app-icon>
            <span>+18.2%</span>
          </div>
          <span class="text-slate-400 dark:text-ink-muted">vs last month</span>
        </div>
      </div>
    </div>
    <div class="bg-white dark:bg-surface-0 dark:border-surface-0 border border-[#F5F5F5] rounded-3xl p-5 shadow-sm relative">
      <app-icon name="gripVertical" [size]="14" class="text-slate-300 absolute top-4 right-4"></app-icon>
      <div class="flex flex-col gap-3 mb-4">
        <div class="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
          <app-icon name="fileText" [size]="18" class="text-white"></app-icon>
        </div>
        <span class="text-[12px] font-medium text-slate-500 dark:text-ink-soft">Contracted Business</span>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-2xl font-bold text-slate-900 dark:text-white">{{ currentBusinessContractsOverall()?.businesses || 0 }}</span>
        <div class="flex items-center gap-1 text-[10px] font-bold">
          <div class="flex items-center gap-1 text-emerald-500">
            <app-icon name="trendingUp" [size]="12"></app-icon>
            <span>+18.2%</span>
          </div>
          <span class="text-slate-400 dark:text-ink-muted">vs last month</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Bottom Actions -->
  <!-- <button
    class="w-full py-4 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-center gap-3 text-slate-900 font-bold transition-all active:scale-[0.98]">
    <app-icon-v2 name="layout-dashboard" [size]="20"></app-icon-v2>
    Customize Dashboard
  </button> -->
</div>
